import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import PageSearchInput from '@/components/Inputs/PageSearchInput';
import LoadMoreButton from '@/components/Buttons/LoadMoreButton';
import AppDashboardFooter from '@/components/AppDashboardFooter';
import TopicTagListItem from '@/components/Topics/TopicTagListItem';
import TopicResearchCallout from '@/components/Topics/TopicResearchCallout';
import { topicTagApiSsr } from '@/lib/topicTagApi';
import { formatCount } from '@/utils/agent_catalog';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 350;

export default function TopicTagsIndexPage() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS, showsScrollIndicator } = useResponsiveness();

  const [tags, setTags] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setDebouncedQuery('');
      return undefined;
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  useEffect(() => {
    let cancelled = false;

    setTags([]);
    setPagination(null);
    setPage(1);
    setLoadError(null);
    setLoading(true);

    (async () => {
      try {
        const res = await topicTagApiSsr.list({
          page: 1,
          limit: PAGE_SIZE,
          q: debouncedQuery || undefined,
        });
        if (cancelled) return;
        setTags(Array.isArray(res?.topicTags) ? res.topicTags : []);
        setPagination(res?.pagination || null);
      } catch (e) {
        if (!cancelled) {
          setTags([]);
          setLoadError(e?.message || 'error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [debouncedQuery]);

  const hasMore = useMemo(() => {
    if (!pagination) return false;
    return page < (pagination.totalPages || 1);
  }, [pagination, page]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const res = await topicTagApiSsr.list({
        page: nextPage,
        limit: PAGE_SIZE,
        q: debouncedQuery || undefined,
      });
      const batch = Array.isArray(res?.topicTags) ? res.topicTags : [];
      setTags((prev) => {
        const seen = new Set(prev.map((row) => row.id));
        return [...prev, ...batch.filter((row) => row?.id && !seen.has(row.id))];
      });
      setPagination(res?.pagination || pagination);
      setPage(nextPage);
    } catch {
      /* keep current list */
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, page, pagination, debouncedQuery]);

  const totalCount = pagination?.total ?? tags.length;

  const styles = useMemo(() => StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.solid_color_primary },
    inner: {
      paddingHorizontal: isXS ? 16 : 24,
      paddingTop: 16,
      paddingBottom: 32,
      maxWidth: 900,
      alignSelf: 'center',
      width: '100%',
      gap: 16,
    },
    meta: { opacity: 0.7 },
    loading: { paddingVertical: 48, alignItems: 'center' },
    loadMore: { marginTop: 8, alignSelf: 'center', minWidth: 220 },
    searchRow: { marginTop: 4 },
  }), [colors, isXS]);

  return (
    <View style={styles.container}>
      <MetaHead
        title={`${t('pages.tags.title')} · Gab AI`}
        description={t('pages.tags.description')}
        canonical="/tags"
      />
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ flexGrow: 1 }}
        showsVerticalScrollIndicator={showsScrollIndicator}
      >
        <View style={styles.inner}>
          <View>
            <Text type="h2">{t('pages.tags.title')}</Text>
            <Text type="p" style={{ opacity: 0.8 }}>{t('pages.tags.description')}</Text>
            {!loading && pagination?.total != null && (
              <Text type="p" style={styles.meta}>
                {formatCount(totalCount)} {totalCount === 1 ? 'tag' : 'tags'}
              </Text>
            )}
          </View>

          <TopicResearchCallout />

          {!loadError && (
            <PageSearchInput
              value={query}
              onChangeText={setQuery}
              placeholder={t('pages.tags.searchPlaceholder')}
              catalogVariant
              noPadding
              containerStyle={styles.searchRow}
            />
          )}

          {loading ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.text_color_secondary} />
            </View>
          ) : loadError ? (
            <Text type="p" style={{ opacity: 0.75 }}>{t('pages.topics.notFound')}</Text>
          ) : (
            <>
              {tags.length === 0 && debouncedQuery ? (
                <Text type="p" style={{ opacity: 0.75 }}>
                  {t('pages.tags.noResultsForQuery').replace('%QUERY%', debouncedQuery)}
                </Text>
              ) : (
                <View style={{ gap: 6 }}>
                  {tags.map((tag) => (
                    <TopicTagListItem
                      key={tag.id}
                      tag={tag}
                      colors={colors}
                      onPress={() => tag.slug && router.push(`/tags/${encodeURIComponent(tag.slug)}`)}
                    />
                  ))}
                </View>
              )}

              {hasMore && tags.length > 0 && (
                <LoadMoreButton
                  onPress={loadMore}
                  isLoading={loadingMore}
                  isLarge
                  style={styles.loadMore}
                />
              )}
            </>
          )}
        </View>
        <View style={{ paddingHorizontal: isXS ? 16 : 24, paddingTop: 8 }}>
          <AppDashboardFooter />
        </View>
      </ScrollView>
    </View>
  );
}
